import { ConnectedPosition } from '@angular/cdk/overlay';
import { ChangeDetectionStrategy, Component, computed, inject, input, output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { SvgIconComponent, injectRegisterIcons } from '@ngneat/svg-icon';

import { TenantsStore } from '@core/stores/tenants/tenants.store';

import { MultiSelectComponent, MultiSelectCustomTriggerDirective, MultiSelectOptionViewModel } from '@shared/components/multi-select';

import { appChevronDown16Icon } from '@icons/chevron-down-16';

interface TenantOption {
  id: string;
  name: string;
}

@Component({
  selector: 'app-tenant-quick-selector',
  imports: [FormsModule, MultiSelectComponent, MultiSelectCustomTriggerDirective, SvgIconComponent],
  templateUrl: './tenant-quick-selector.component.html',
  host: { class: 'flex flex-col' },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TenantQuickSelectorComponent {
  readonly #tenantsStore = inject(TenantsStore);

  public selectedTenantId = input<string>();
  public readonly tenantChange = output<string>();

  public readonly tenantOptions = computed((): MultiSelectOptionViewModel<TenantOption>[] =>
    (this.#tenantsStore.tenants() ?? []).map((tenant) => ({
      label: tenant.name,
      value: { id: tenant.id, name: tenant.name },
    }))
  );
  public readonly selectedTenant = computed(
    () => this.tenantOptions().find((option) => option.value.id === this.selectedTenantId())?.value ?? null
  );
  public readonly isLoading = this.#tenantsStore.isLoading;
  public readonly hasMultipleTenants = computed(() => this.tenantOptions().length > 1);
  public readonly positions: ConnectedPosition[] = [
    { originX: 'end', originY: 'bottom', overlayX: 'end', overlayY: 'top', offsetY: 16 },
    { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top', offsetY: 16 },
  ];

  constructor() {
    this.#tenantsStore.ensureTenantsLoaded();
    injectRegisterIcons(appChevronDown16Icon);
  }

  public onTenantChange(tenant: TenantOption | null): void {
    if (tenant && !this.isLoading()) {
      this.tenantChange.emit(tenant.id);
    }
  }
}
